import type { ChatContentBlock } from '@/hooks/useWebSocket';
import type { StreamingState } from './chatStore';

export type ChatStreamProvider = string;

export interface ChatStreamRecord {
  streamId: string;
  sessionId: string;
  provider: ChatStreamProvider;
  state: StreamingState;
  text: string;
  content: ChatContentBlock[];
  error: string | null;
  startTime: number | null;
  elapsed: number;
  activeToolName: string | null;
  activeToolId: string | null;
  lastActivityTime: number | null;
}

export interface ChatStreamCollection {
  activeStreamsBySession: Record<string, string>;
  streamsById: Record<string, ChatStreamRecord>;
}

export function createChatStreamRecord(
  streamId: string,
  sessionId: string,
  provider: ChatStreamProvider = 'claude'
): ChatStreamRecord {
  return {
    streamId,
    sessionId,
    provider,
    state: 'connecting',
    text: '',
    content: [],
    error: null,
    startTime: null,
    elapsed: 0,
    activeToolName: null,
    activeToolId: null,
    lastActivityTime: null,
  };
}

export function getSessionStream(
  collection: ChatStreamCollection,
  sessionId: string | null | undefined
): ChatStreamRecord | null {
  if (!sessionId) return null;
  const streamId = collection.activeStreamsBySession[sessionId];
  return streamId ? collection.streamsById[streamId] ?? null : null;
}

export function isRunningStream(stream: ChatStreamRecord | null | undefined): boolean {
  return stream?.state === 'streaming' || stream?.state === 'connecting';
}

export function removeStream(
  collection: ChatStreamCollection,
  streamId: string
): ChatStreamCollection {
  const stream = collection.streamsById[streamId];
  if (!stream) return collection;

  const nextStreams = { ...collection.streamsById };
  delete nextStreams[streamId];

  const nextBySession = { ...collection.activeStreamsBySession };
  if (nextBySession[stream.sessionId] === streamId) {
    delete nextBySession[stream.sessionId];
  }

  return {
    activeStreamsBySession: nextBySession,
    streamsById: nextStreams,
  };
}
